// Тонкая обёртка над `fetch` для нового owner web.
//
// Backend живёт по тому же origin (Express отдаёт frontend dist + /api/* + SSE),
// поэтому baseURL пустой — относительные пути типа `/api/agents/health`.
//
// Контракт ошибок (handoff `02-api-контракт.md` §0):
//   { error: { code: string, message: string, hint?: string } } | { error: string }
// Прокидываем как ApiError; HTTP-статус доступен через `error.status`.
//
// Этот файл — только примитив `apiFetch` + типы. TanStack Query hooks (`useAgents`,
// `useSkills` и т.д.) будут жить рядом — в `src/api/agents.ts`, `src/api/skills.ts`
// по мере раскрытия экранов на Шагах 3-11.

export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined
  readonly hint: string | undefined

  constructor(message: string, status: number, code?: string, hint?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.hint = hint
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body' | 'headers'> {
  /** JSON-body — будет сериализован + Content-Type подставится автоматически. */
  json?: unknown
  /** Доп. headers — мерджатся поверх дефолтных. */
  headers?: Record<string, string>
  /** Query-параметры — мерджатся в URL. `undefined`-значения пропускаются. */
  query?: Record<string, string | number | boolean | null | undefined>
}

const DEFAULT_HEADERS: Record<string, string> = {
  Accept: 'application/json',
}

function buildUrl(path: string, query?: ApiFetchOptions['query']): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  if (!qs) return path
  return path.includes('?') ? `${path}&${qs}` : `${path}?${qs}`
}

/**
 * Главная точка вызова API.
 *
 * Дёргает `/api/...`, возвращает распарсенный JSON. При не-2xx бросает
 * `ApiError` с распарсенным `{ error.code, error.message, error.hint }`.
 * Сессия SSO передаётся cookie'й `frm_session`, поэтому `credentials: 'include'`
 * стоит по умолчанию (тот же origin, но явность не мешает).
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { json, headers, query, ...rest } = options

  const requestInit: RequestInit = {
    credentials: 'same-origin',
    ...rest,
    headers: {
      ...DEFAULT_HEADERS,
      ...(headers ?? {}),
    },
  }
  if (json !== undefined) {
    requestInit.body = JSON.stringify(json)
    ;(requestInit.headers as Record<string, string>)['Content-Type'] = 'application/json'
  }

  const url = buildUrl(path, query)
  const response = await fetch(url, requestInit)

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')

  if (!response.ok) {
    // Пытаемся достать структурированное сообщение об ошибке.
    let code: string | undefined
    let message = `${response.status} ${response.statusText}`
    let hint: string | undefined
    if (isJson) {
      try {
        const body = (await response.json()) as
          | {
              error?: string | { code?: string; message?: string; hint?: string }
              message?: string
              feature?: string
            }
          | undefined
        const errorField = body?.error
        if (typeof errorField === 'string') {
          code = errorField
          message = body?.message || errorField
          hint = body?.feature
        } else if (errorField && typeof errorField === 'object') {
          code = errorField.code
          if (errorField.message) message = errorField.message
          hint = errorField.hint
        }
      } catch {
        /* fallthrough: используем дефолтное message */
      }
    }
    throw new ApiError(message, response.status, code, hint)
  }

  if (isJson) {
    return (await response.json()) as T
  }
  // Не-JSON: возвращаем как текст (например, markdown из Vite `?raw` не относится сюда,
  // но `/api/skills/:name/raw` теоретически может вернуть text/plain).
  return (await response.text()) as unknown as T
}
